/**
 * Start — two screens, one question each: who is it for, and which vibe.
 */
import { useTranslation } from "react-i18next";
import { orderedThemesFor } from "../../themes/orderedThemes";
import Icon from "../chassis/Icon";
import ThemeArt from "../themeArt";
import { BigField, Heading, PrimaryButton, Sub } from "../chassis/widgets";
import { C, FONT, TINT } from "../chassis/tokens";

export function NameScreen({ state, dispatch }) {
  const { t: tr } = useTranslation();
  return (
    <>
      <div style={{ width: 58, height: 58, borderRadius: 18, background: "linear-gradient(145deg, #FF7AA2 0%, #D8406E 60%, #B9245A 100%)", boxShadow: "0 14px 28px -12px rgba(194,47,91,0.65), inset 0 1px 0 rgba(255,255,255,0.35)", display: "grid", placeItems: "center", transform: "rotate(-6deg)" }}>
        <span style={{ fontSize: 30, fontWeight: 800, color: C.white, letterSpacing: "-0.03em" }}>P</span>
      </div>

      <div style={{ marginTop: 26, fontSize: 12, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", color: C.accent }}>
        {tr("app.creator.name.eyebrow")}
      </div>
      <Heading size={40} accent={tr("app.creator.name.headingAccent")}>{tr("app.creator.name.heading")}</Heading>
      <Sub>{tr("app.creator.name.sub")}</Sub>

      <BigField
        id="creator-name"
        label={tr("app.creator.name.label")}
        value={state.recipientName}
        onChange={(v) => dispatch({ type: "setName", value: v })}
        placeholder={tr("app.creator.name.placeholder")}
        fontSize={30}
      />

      <p style={{ margin: "16px 4px 0", fontSize: 13, lineHeight: 1.5, color: C.faint }}>{tr("app.creator.name.note")}</p>
      <div style={{ height: 24 }} />
    </>
  );
}

// Also used by the app's Home (src/features/home), with its own test id.
export function VibeCard({ themeKey, label, tagline, selected, locked, tilt, onSelect, testId, until }) {
  const { t: tr, i18n } = useTranslation();
  const untilText = until
    ? tr("app.creator.seasonal.until", {
        date: new Intl.DateTimeFormat(i18n.resolvedLanguage || i18n.language, { day: "numeric", month: "numeric" }).format(until),
      })
    : null;
  return (
    <button
      type="button"
      data-testid={testId || `creator-vibe-${themeKey}`}
      aria-pressed={selected}
      onClick={onSelect}
      style={{
        position: "relative", textAlign: "left", borderRadius: 22, background: C.surface, padding: "8px 8px 12px",
        cursor: "pointer", transform: `rotate(${tilt}deg)`, fontFamily: FONT,
        border: selected ? `2px solid ${C.accent}` : `1.5px solid ${C.line}`,
        boxShadow: selected
          ? `0 0 0 5px ${TINT.accent(0.10)}, 0 18px 36px -18px ${TINT.accent(0.45)}`
          : `0 16px 32px -20px ${TINT.shadow(0.35)}`,
      }}
    >
      {selected && (
        <span style={{ position: "absolute", top: -8, right: -8, width: 26, height: 26, borderRadius: 999, background: C.accent, display: "grid", placeItems: "center", boxShadow: `0 6px 12px -4px ${TINT.accent(0.6)}`, zIndex: 2 }}>
          <Icon name="check" size={13} color={C.onAccent} strokeWidth={3.4} />
        </span>
      )}
      {(locked || untilText) && (
        <div style={{ position: "absolute", top: 16, right: 16, zIndex: 2, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
          {locked && (
            <span style={{ display: "flex", alignItems: "center", gap: 3, padding: "3px 7px", borderRadius: 999, background: TINT.shadow(0.85) }}>
              <Icon name="lock" size={9} color={C.white} strokeWidth={2.8} />
              <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.08em", color: C.white }}>{tr("app.creator.pro")}</span>
            </span>
          )}
          {untilText && (
            <span data-testid={`vibe-until-${themeKey}`} style={{ whiteSpace: "nowrap", padding: "3px 8px", borderRadius: 999, background: TINT.accent(0.92), fontSize: 9, fontWeight: 800, letterSpacing: "0.04em", color: C.onAccent }}>
              {untilText}
            </span>
          )}
        </div>
      )}
      <ThemeArt theme={themeKey} />
      <span style={{ display: "block", margin: "10px 6px 0", fontSize: 15, fontWeight: 800, color: C.ink }}>{label}</span>
      <span style={{ display: "block", margin: "1px 6px 0", fontSize: 12, color: C.soft }}>{tagline}</span>
    </button>
  );
}

export function VibeScreen({ state, dispatch, themes, isPro, onLocked, now = new Date(), allSeasons = false }) {
  const { t: tr } = useTranslation();
  const entries = orderedThemesFor(themes, now, { currentTheme: state.theme, allSeasons });
  return (
    <>
      <div style={{ padding: "0 4px" }}>
        <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", color: C.accent }}>
          {tr("app.creator.name.eyebrow")}
        </div>
        <Heading size={32} accent={state.recipientName}>{tr("app.creator.vibe.heading")}</Heading>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 14, margin: "20px 0 8px" }}>
        {entries.map(({ key, until }, i) => {
          const locked = themes[key].locked === true && !isPro;
          return (
            <VibeCard
              key={key}
              themeKey={key}
              until={until}
              label={tr(`app.themes.${key}.name`, { defaultValue: themes[key].name })}
              tagline={tr(`app.themes.${key}.description`, { defaultValue: themes[key].description })}
              selected={state.theme === key}
              locked={locked}
              tilt={i % 2 === 0 ? -1.2 : 1.1}
              onSelect={() => (locked ? onLocked(key) : dispatch({ type: "setTheme", value: key }))}
            />
          );
        })}
      </div>
    </>
  );
}

export function startFooter({ state, tr, onNext, step }) {
  if (step === "name") {
    return (
      <PrimaryButton testId="creator-next" onClick={onNext} disabled={!state.recipientName.trim()}>
        {tr("app.creator.common.next")}
      </PrimaryButton>
    );
  }
  return (
    <PrimaryButton testId="creator-next" onClick={onNext} disabled={!state.theme}>
      {tr("app.creator.vibe.cta")}
    </PrimaryButton>
  );
}
